/**
 * Sprint O2 — Quick-Open command palette.
 *
 * Mission alignment: open any order in <3 clicks (here: 2 — Cmd+K + Enter).
 *
 * Triggered by `/` or `Cmd+K` / `Ctrl+K` from anywhere in the admin dashboard.
 * Searches across:
 *   - Orders (invoice number, company, customer)
 *   - Master catalog products (SKU, name)
 *
 * On Enter / click:
 *   - Order → invokes `onOpenOrder(order)` to launch OrderDetailsModal
 *   - Product → invokes `onOpenProduct(product)` (caller decides where to go)
 *
 * No backend changes — pulls from existing /orders + /financial-products.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";

export default function QuickOpenModal({ open, onClose, onOpenOrder, onOpenProduct }) {
  const { t } = useI18n();
  const [query, setQuery] = useState("");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);

  // Load both lists once when the modal opens. The result count is small
  // (hundreds at most) so client-side filtering keeps the UX instant.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActiveIdx(0);
    Promise.all([
      axios.get(`${API}/orders`).then((r) => r.data || []).catch(() => []),
      axios.get(`${API}/financial-products?include_inactive=false`).then((r) => {
        const d = r.data;
        return Array.isArray(d) ? d : d?.items || [];
      }).catch(() => []),
    ]).then(([o, p]) => {
      setOrders(o);
      setProducts(p);
    });
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // Show most recent 8 orders when query is empty
      return orders.slice(0, 8).map((o) => ({ type: "order", o }));
    }
    const matchOrder = (o) => {
      const hay = [
        o.invoice_number,
        o.company_name,
        o.customer_name,
        o.contact_person,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    };
    const matchProduct = (p) => {
      const hay = [p.sku, p.name, p.description].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    };
    const ord = orders.filter(matchOrder).slice(0, 8).map((o) => ({ type: "order", o }));
    const prods = products.filter(matchProduct).slice(0, 6).map((p) => ({ type: "product", p }));
    return [...ord, ...prods];
  }, [query, orders, products]);

  useEffect(() => {
    if (activeIdx >= results.length) setActiveIdx(0);
  }, [results.length, activeIdx]);

  if (!open) return null;

  const pick = (r) => {
    if (r.type === "order") onOpenOrder?.(r.o);
    if (r.type === "product") onOpenProduct?.(r.p);
    onClose();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const r = results[activeIdx];
      if (r) pick(r);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4"
      onClick={onClose}
      data-testid={DASHBOARD.quickOpenModal}
    >
      <div
        className="bg-white border-2 border-black w-full max-w-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="border-b border-[var(--border)] p-3">
          <input
            ref={inputRef}
            data-testid={DASHBOARD.quickOpenInput}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar # factura, empresa, cliente, SKU…"
            className="w-full bg-transparent text-lg font-display focus:outline-none"
          />
          <div className="text-[0.65rem] font-mono-tech tracking-wider text-[var(--text-secondary)] mt-1">
            ↑↓ navegar · ↵ abrir · ESC cerrar
          </div>
        </div>
        <ul className="max-h-[60vh] overflow-y-auto divide-y divide-[var(--border)]">
          {results.length === 0 ? (
            <li className="p-6 text-center text-sm text-[var(--text-secondary)]">
              Sin resultados
            </li>
          ) : (
            results.map((r, i) => {
              const isActive = i === activeIdx;
              const key =
                r.type === "order" ? `o-${r.o.id}` : `p-${r.p.id}`;
              return (
                <li
                  key={key}
                  data-testid={DASHBOARD.quickOpenResult(i)}
                  onClick={() => pick(r)}
                  onMouseEnter={() => setActiveIdx(i)}
                  className={`p-3 cursor-pointer flex items-center justify-between gap-3 ${
                    isActive ? "bg-[var(--surface-alt)]" : ""
                  }`}
                >
                  {r.type === "order" ? (
                    <>
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-bold truncate">
                          #{r.o.invoice_number || r.o.id?.slice(0, 6)} · {r.o.company_name || "—"}
                        </div>
                        <div className="text-xs text-[var(--text-secondary)] truncate">
                          {r.o.customer_name || ""}
                          {r.o.delivery_date ? ` · entrega ${r.o.delivery_date}` : ""}
                          {` · ${r.o.status || ""}`}
                        </div>
                      </div>
                      <span className="px-2 py-0.5 text-[0.6rem] font-mono-tech border border-black tracking-widest">
                        ORDEN
                      </span>
                    </>
                  ) : (
                    <>
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-bold truncate">
                          {r.p.sku ? `${r.p.sku} · ` : ""}{r.p.name}
                        </div>
                        <div className="text-xs text-[var(--text-secondary)] truncate">
                          {r.p.description || ""}
                        </div>
                      </div>
                      <span className="px-2 py-0.5 text-[0.6rem] font-mono-tech border border-black tracking-widest">
                        SKU
                      </span>
                    </>
                  )}
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
}
